import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import InvoicePreview from '../../components/InvoicePreview';
import {
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { PayoutStatusCard } from '../../components/fraud';
import { PayoutService } from '../../services/PayoutService';

export default function PreviewInvoice() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPublicView, setIsPublicView] = useState(false);
  const [isInvoiceCreator, setIsInvoiceCreator] = useState(false);
  const [capabilityCheckPerformed, setCapabilityCheckPerformed] = useState(false); // eslint-disable-line no-unused-vars
  const [hasStripeCapabilityIssue, setHasStripeCapabilityIssue] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const auth = getAuth();

  const handleUpdateInvoice = async (updatedInvoiceData) => {
    try {
      const firestore = getFirestore();
      const invoiceRef = doc(firestore, 'invoices', invoiceId);
      await updateDoc(invoiceRef, {
        fromName: updatedInvoiceData.fromName,
        clientName: updatedInvoiceData.clientName,
        updatedAt: new Date().toISOString()
      });
      setInvoiceDetails(prev => ({
        ...prev,
        fromName: updatedInvoiceData.fromName,
        clientName: updatedInvoiceData.clientName
      }));
      toast.success('Invoice updated');
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Failed to save');
    }
  };

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      setIsLoading(true);
      try {
        const firestore = getFirestore();
        const invoiceRef = doc(firestore, "invoices", invoiceId);
        const docSnap = await getDoc(invoiceRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.dueDate) {
            try {
              if (data.dueDate.toDate) {
                data.dueDate = data.dueDate.toDate();
              } else {
                data.dueDate = new Date(data.dueDate);
              }
            } catch (err) {
              data.dueDate = null;
            }
          }
          setInvoiceDetails(data);
          const currentUser = auth.currentUser;
          setIsInvoiceCreator(currentUser && data.userId === currentUser.uid);
          if (currentUser && data.userId === currentUser.uid && data.connectedAccountId) {
            checkStripeCapabilities(data.connectedAccountId);
          } else {
            setCapabilityCheckPerformed(true);
          }
        } else {
          throw new Error("Invoice not found");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsPublicView(!user);
      fetchInvoiceDetails();
    });

    return () => unsubscribe();
  }, [invoiceId, auth]);

  // Fetch payout status for paid invoices when the creator is viewing
  useEffect(() => {
    if (!invoiceDetails || invoiceDetails.status !== 'paid' || !isInvoiceCreator) return;

    const fetchPayoutStatus = async () => {
      try {
        const payoutSvc = new PayoutService();
        const status = await payoutSvc.getInvoicePayoutStatus(invoiceId);
        if (status) setPayoutStatus(status);
      } catch (error) {
        console.error('Error fetching payout status:', error);
      }
    };

    fetchPayoutStatus();
  }, [invoiceId, invoiceDetails, isInvoiceCreator]);

  const checkStripeCapabilities = async (accountId) => {
    try {
      const response = await fetch(`https://klorah-fast-server-my8l.onrender.com/api/check-account-capabilities/${accountId}`);
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error &&
            (errorData.error.toLowerCase().includes('capabilities') ||
             errorData.error.toLowerCase().includes('transfers'))) {
          setHasStripeCapabilityIssue(true);
        }
      }
    } catch (error) {
      console.error("Error checking Stripe capabilities:", error);
    } finally {
      setCapabilityCheckPerformed(true);
    }
  };

  const copyPaymentLink = () => {
    const link = `https://klorah.app/payment/${invoiceId}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      toast.success("Payment link copied");
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  const getAmount = () => {
    if (invoiceDetails?.total) return invoiceDetails.total;
    return parseFloat(invoiceDetails?.amount || invoiceDetails?.price || 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoiceDetails) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-black flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm w-full bg-white dark:bg-[#111111] rounded-2xl p-8 text-center shadow-sm border border-gray-100 dark:border-[#222]"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl flex items-center justify-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {error || 'Invoice not found'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            This invoice may have been deleted or moved.
          </p>
          <motion.button
            onClick={() => navigate('/app')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm"
          >
            Back to Dashboard
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const isPaid = invoiceDetails.status === 'paid';

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-black">
      {/* Header - Fixed on mobile */}
      <div className="sticky top-0 z-20 bg-[#FAFAFA]/80 dark:bg-black/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-[#222]">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-14 flex items-center justify-between">
          {/* Back button - only for logged in users */}
          {!isPublicView ? (
            <button
              onClick={() => navigate('/app')}
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
          ) : (
            <div className="w-12" />
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-[120px] sm:max-w-none">
            {invoiceId}
          </p>

          {/* Mobile-only quick actions */}
          <div className="flex items-center gap-2 lg:hidden">
            {/* Copy link - only for invoice creator */}
            {isInvoiceCreator && (
              <button
                onClick={copyPaymentLink}
                className="p-2 text-gray-500 hover:text-gray-900 transition-colors"
              >
                {linkCopied ? (
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                ) : (
                  <ClipboardDocumentIcon className="w-5 h-5" />
                )}
              </button>
            )}
            {/* Pay button - show for everyone when unpaid */}
            {!isPaid && !hasStripeCapabilityIssue && (
              <button
                onClick={() => navigate(`/payment/${invoiceId}`)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-brand rounded-lg"
              >
                Pay
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left Column - Invoice Preview (Desktop: larger, always visible) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 order-2 lg:order-1"
          >
            <div className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-100 dark:border-[#222] shadow-sm overflow-hidden">
              <InvoicePreview
                invoiceData={invoiceDetails}
                invoiceId={invoiceId}
                onUpdateInvoice={isInvoiceCreator ? handleUpdateInvoice : null}
              />
            </div>
          </motion.div>

          {/* Right Column - Summary Card (Desktop: sticky sidebar) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="w-full lg:w-80 xl:w-96 order-1 lg:order-2 flex-shrink-0"
          >
            <div className="lg:sticky lg:top-20">
              <div className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-100 dark:border-[#222] shadow-sm overflow-hidden">

                {/* Status & Title */}
                <div className="p-5 border-b border-gray-100 dark:border-[#222]">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                      {invoiceDetails.project || invoiceDetails.gig || 'Invoice'}
                    </h1>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${
                      isPaid
                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
                    }`}>
                      {isPaid ? 'Paid' : 'Due'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {invoiceDetails.clientName || invoiceDetails.client || invoiceDetails.customer || 'Client'}
                  </p>
                </div>

                {/* Amount - Prominent */}
                <div className="p-5 bg-gray-50/50 dark:bg-[#0D0D0D] border-b border-gray-100 dark:border-[#222]">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Total Amount</p>
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                    {invoiceDetails.currency || 'USD'} {getAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                {/* Payout Status - shown to creator for paid invoices */}
                {isPaid && isInvoiceCreator && payoutStatus && (
                  <div className="p-4 border-b border-gray-100 dark:border-[#222]">
                    <PayoutStatusCard payoutStatus={payoutStatus} />
                  </div>
                )}

                {/* Details Grid */}
                <div className="p-5 border-b border-gray-100 dark:border-[#222] space-y-3">
                  {invoiceDetails.dueDate && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                        <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Due Date</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {invoiceDetails.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  )}

                  {invoiceDetails.fromName && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">From</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{invoiceDetails.fromName}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stripe Warning */}
                <AnimatePresence>
                  {isInvoiceCreator && hasStripeCapabilityIssue && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="p-4 bg-red-50 border-b border-red-100"
                    >
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-red-800">Stripe setup required</p>
                          <button
                            onClick={() => window.open('https://connect.stripe.com/app/express', '_blank')}
                            className="text-xs text-red-600 hover:text-red-700 underline mt-0.5"
                          >
                            Update account →
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Actions - Desktop */}
                <div className="p-5 space-y-2 hidden lg:block dark:border-t dark:border-[#222]">
                  {/* Pay button - show for everyone when unpaid */}
                  {!isPaid && !hasStripeCapabilityIssue && (
                    <motion.button
                      onClick={() => navigate(`/payment/${invoiceId}`)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm flex items-center justify-center gap-2"
                    >
                      <CreditCardIcon className="w-4 h-4" />
                      Pay Now
                    </motion.button>
                  )}

                  {/* Copy link - only for invoice creator */}
                  {isInvoiceCreator && (
                    <motion.button
                      onClick={copyPaymentLink}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="w-full py-2.5 px-4 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#222] text-gray-700 dark:text-gray-200 font-medium rounded-xl transition-all text-sm flex items-center justify-center gap-2"
                    >
                      {linkCopied ? (
                        <>
                          <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                          Link Copied
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-4 h-4" />
                          Copy Payment Link
                        </>
                      )}
                    </motion.button>
                  )}
                </div>

                {/* Fee Notice — only shown when customer pays the fee */}
                {isInvoiceCreator && invoiceDetails.feePayer === 'customer' && (
                  <div className="px-5 pb-4 pt-0 lg:pt-0">
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      Customer pays 2.9% fee
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
